/* The enquiry form.
 *
 * It asks the three attribution questions separately and it refuses to invent an
 * answer to any of them. "Something else…" is not an Other bucket: picking it asks
 * for the actual words and keeps them. The migration found 185 answers thrown into a
 * bucket called Others and 351 real reasons typed into a column nobody reported on,
 * and this form is the reason that cannot happen again here.
 *
 * WHAT IT CANNOT SET: the stage, the anchor, the next action. A lead starts at new
 * with nobody attached, because a form submission is not a relationship and an
 * enquiry that arrives at "both trials done" is a record that lies. */
import { useState } from 'react';
import type { Lead, Staff } from '@acrostic/model';
import { useAsync, useSeesCommercials, useSource } from '../../data/SourceProvider.js';
import { Field, Modal } from '../../components/Modal.js';
import { useToast } from '../../components/Toast.js';
import { HEARD_FREETEXT, HEARD_OPTIONS, SOURCE_CATEGORIES, SOURCE_SUBS } from '../../lib/leads.js';

export function NewLead({ onClose, onAdded }: { onClose: () => void; onAdded: (l: Lead) => void }) {
  const source = useSource();
  const toast = useToast();
  const money = useSeesCommercials();
  const { data: staff } = useAsync(() => source.getStaff(), [source]);
  const desk: Staff[] = (staff ?? []).filter((s) => s.kind === 'support' && s.active);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [cat, setCat] = useState('');
  const [sub, setSub] = useState('');
  const [heard, setHeard] = useState('');
  const [heardOther, setHeardOther] = useState('');
  const [value, setValue] = useState('299');
  const [ownerId, setOwnerId] = useState('');
  const [saving, setSaving] = useState(false);

  const subs = SOURCE_SUBS[cat] ?? [];

  async function save() {
    const full = name.trim().replace(/\s+/g, ' ');
    if (!full) { toast('A name is required', 'bad'); return; }
    if (!cat) { toast('Say how they arrived', 'bad'); return; }
    if (heard === HEARD_FREETEXT && !heardOther.trim()) {
      toast('Tell us how they heard about us, or pick "not asked"', 'bad'); return;
    }
    const [first = '', ...rest] = full.split(' ');
    setSaving(true);
    try {
      const lead = await source.createLead({
        firstName: first, lastName: rest.join(' '),
        email: email.trim() || null,
        phone: phone.trim() || null,
        source: cat,
        sourceDetail: sub || null,
        heard: heard === HEARD_FREETEXT ? heardOther.trim() : (heard || null),
        interest: null,
        valueCents: money && Number(value) > 0 ? Math.round(Number(value) * 100) : null,
        ownerId: ownerId || null,
      });
      onAdded(lead);
    } catch (e) {
      setSaving(false);
      toast((e as Error).message, 'bad');
    }
  }

  return (
    <Modal
      title="New lead" onClose={onClose}
      footer={
        <>
          <button type="button" className="btn t-label-medium" onClick={onClose}>Cancel</button>
          <button type="button" className="btn primary t-label-medium" disabled={saving} onClick={() => void save()}>
            {saving ? 'Adding…' : 'Add lead'}
          </button>
        </>
      }
    >
      <Field label="Name">
        <input className="field" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>
      <div className="grid2">
        <Field label="Email">
          <input className="field" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Phone">
          <input className="field" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </Field>
      </div>

      <div className="grid2">
        <Field label="How they arrived" hint="The mechanism, not the advertising.">
          <select className="field" value={cat} onChange={(e) => { setCat(e.target.value); setSub(''); }}>
            <option value="">Pick one</option>
            {SOURCE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="What drove them to it">
          <select className="field" value={sub} disabled={!subs.length}
            onChange={(e) => setSub(e.target.value)}>
            <option value="">{subs.length ? 'Not recorded' : 'Nothing to pick for this source'}</option>
            {subs.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </Field>
      </div>

      <div className="grid2">
        {/* NOT ASKED ON A SEAT THAT WILL NEVER BE SHOWN IT. A field somebody fills
            in and then cannot read back is a field they will stop filling in. The
            source refuses the value from this seat as well. */}
        {money ? (
          <Field label="Est. value ($/mo)">
            <input className="field" type="number" min="0" value={value} onChange={(e) => setValue(e.target.value)} />
          </Field>
        ) : <div />}
        <Field label="Route to" hint="Whose call this is. Not a relationship: that is earned.">
          <select className="field" value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
            <option value="">Route later</option>
            {desk.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
      </div>

      <Field
        label="How did you hear about us?"
        hint="What made them aware of the gym at all. This is the referral number."
      >
        <select className="field" value={heard} onChange={(e) => setHeard(e.target.value)}>
          <option value="">Not asked</option>
          {HEARD_OPTIONS.map((h) => <option key={h} value={h}>{h}</option>)}
          <option value={HEARD_FREETEXT}>Something else…</option>
        </select>
      </Field>
      {heard === HEARD_FREETEXT && (
        <input
          className="field" style={{ marginTop: 6 }} autoFocus
          placeholder="In their words" value={heardOther}
          onChange={(e) => setHeardOther(e.target.value)}
        />
      )}
    </Modal>
  );
}
